"use client";

import { useCallback, useEffect, useState } from "react";
import type { components } from "@theta/api-client";
import { thetaApi, dataOf, pageOf, unwrapV1 } from "@/lib/api/theta";

type PushSubscriptionRow = components["schemas"]["PushSubscription"];

/**
 * WEB PUSH, ON /api/v1 — §1.8, QN2, QN7
 *
 * THREE LEGACY ROUTES BECOME ONE COLLECTION:
 *
 *   GET  /theta/push-subscriptions/check?endpoint=  ->  GET /me/push-subscriptions?endpoint=
 *   GET  /theta/push-subscriptions/vapid-public-key ->  GET /meta.pushPublicKey
 *   POST /theta/push-subscriptions                  ->  POST /me/push-subscriptions  ({kind: "web"})
 *   DELETE /theta/push-subscriptions {endpoint}     ->  DELETE /me/push-subscriptions/{id}
 *
 * The `check` route answered `{subscribed: boolean}`; the filter answers the
 * ROW — which is what unsubscribing needs, because a DELETE is addressed by id
 * now rather than by a body on a DELETE. So "am I subscribed?" and "what do I
 * revoke?" stopped being two questions.
 *
 * The body is a STRICT discriminated union on `kind`, replacing the legacy
 * "tell the two transports apart by which field happens to be present"
 * contract; web and Expo devices live in one list. The write is an UPSERT
 * keyed on the endpoint, so a device MOVES to whoever signs in on it and the
 * error counters reset.
 *
 * THE VAPID KEY IS PLATFORM METADATA, not a route of its own — anonymous,
 * cacheable, and it changes on deploy.
 *
 * `public/sw.js` IS ALREADY DONE, and this note is what is left of the handoff
 * that said it was not. It used to read: "sw.js is NOT touched here, it still
 * fetches `/theta/notifications`, and it must move to `/api/v1/notifications`
 * in the same commit that deletes `/theta/*`". All three claims are false now
 * and the last one was never right: the badge counter moved to
 * `GET /api/v1/me`, which carries `badges.unreadNotificationCount` and
 * `badges.unreadConversationCount` as two precomputed integers, rather than to
 * the notification LIST — so the worker no longer pages a collection to count
 * rows on the client, and the app-icon badge and the bell add up the same two
 * numbers (`unreadBadgeTotal`) and therefore cannot disagree.
 *
 * The note is kept rather than deleted because a Step 4 author greps for
 * exactly this sentence, and finding nothing is indistinguishable from finding
 * the work undone. `public/sw.js` names no `/theta/*` route; `pnpm ratchets`
 * walks every `.js` under `public/` and fails the build if one comes back.
 */

function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const rawData = atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

function arrayBufferToBase64(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer);
  let binary = "";
  for (let i = 0; i < bytes.byteLength; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary);
}

/** The row for THIS browser's endpoint, or null. `?endpoint=` is the filter. */
async function findSubscription(endpoint: string): Promise<PushSubscriptionRow | null> {
  const page = pageOf<PushSubscriptionRow>(
    await thetaApi.http.GET("/me/push-subscriptions", {
      params: { query: { kind: "web", endpoint } },
    }),
  );
  return page.items[0] ?? null;
}

interface PushState {
  supported: boolean;
  permission: NotificationPermission | "unsupported";
  subscribed: boolean;
  loading: boolean;
}

export function usePushSubscription() {
  const [state, setState] = useState<PushState>({
    supported: false,
    permission: "unsupported",
    subscribed: false,
    loading: true,
  });

  useEffect(() => {
    async function check() {
      if (!("serviceWorker" in navigator) || !("PushManager" in window)) {
        setState({
          supported: false,
          permission: "unsupported",
          subscribed: false,
          loading: false,
        });
        return;
      }

      const permission = Notification.permission;
      let subscribed = false;

      if (permission === "granted") {
        try {
          const reg = await navigator.serviceWorker.ready;
          const sub = await reg.pushManager.getSubscription();
          if (sub) subscribed = (await findSubscription(sub.endpoint)) !== null;
        } catch {
          // ignore
        }
      }

      setState({ supported: true, permission, subscribed, loading: false });
    }
    check();
  }, []);

  const subscribe = useCallback(async () => {
    setState((s) => ({ ...s, loading: true }));
    try {
      const permission = await Notification.requestPermission();
      if (permission !== "granted") {
        setState((s) => ({ ...s, permission, loading: false }));
        return false;
      }

      // Platform metadata, not a push route (§2.1).
      const { pushPublicKey } = await thetaApi.meta();
      if (!pushPublicKey) {
        // This deployment has no VAPID key configured. Subscribing would
        // produce a browser-side subscription nothing can ever deliver to.
        setState((s) => ({ ...s, permission, loading: false }));
        return false;
      }

      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(pushPublicKey).buffer as ArrayBuffer,
      });

      dataOf<PushSubscriptionRow>(
        await thetaApi.http.POST("/me/push-subscriptions", {
          body: {
            kind: "web",
            endpoint: sub.endpoint,
            keys: {
              p256dh: arrayBufferToBase64(sub.getKey("p256dh")!),
              auth: arrayBufferToBase64(sub.getKey("auth")!),
            },
          },
        }),
      );

      setState({
        supported: true,
        permission: "granted",
        subscribed: true,
        loading: false,
      });
      return true;
    } catch (err) {
      console.error("Push subscription failed:", err);
      setState((s) => ({ ...s, loading: false }));
      return false;
    }
  }, []);

  const unsubscribe = useCallback(async () => {
    setState((s) => ({ ...s, loading: true }));
    try {
      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.getSubscription();
      if (sub) {
        // Find the row before dropping the browser-side subscription: the
        // endpoint is the only handle on it, and `sub.unsubscribe()` is what
        // takes the endpoint away.
        const row = await findSubscription(sub.endpoint);
        if (row) {
          // 204 for a row that is yours, one that is not and one that never
          // existed — a status difference here would be a device oracle.
          unwrapV1<null>(
            await thetaApi.http.DELETE("/me/push-subscriptions/{id}", {
              params: { path: { id: row.id } },
            }),
          );
        }
        await sub.unsubscribe();
      }
      setState({
        supported: true,
        permission: Notification.permission,
        subscribed: false,
        loading: false,
      });
    } catch (err) {
      console.error("Push unsubscribe failed:", err);
      setState((s) => ({ ...s, loading: false }));
    }
  }, []);

  return { ...state, subscribe, unsubscribe };
}
